import { ApolloError } from '@apollo/client';
import { logger } from './logger';

export interface HandleErrorParams<T = unknown> {
  error: T;
  context?: ErrorHandlerContext;
  options?: ErrorHandlerOptions;
}

/**
 * Context information for error handling to provide meaningful logging
 */
export type ErrorHandlerContext = Record<string, any>;

/**
 * Options to customize error handling behavior
 */
export interface ErrorHandlerOptions {
  /** Custom error message for logging */
  customErrorMessage?: string;
  /** Whether to include stack traces in logs (defaults to true) */
  includeStackTrace?: boolean;
}

/**
 * Result of a function with error handling
 */
export interface TryFnResult<T> {
  /** The successful result data */
  data?: T;
  /** The error that occurred, if any */
  error?: unknown;
  /** Whether the function was successful */
  success: boolean;
}

type TryFn<T> = () => T | Promise<T>;

export interface TryFnOptions {
  context?: ErrorHandlerContext;
  errorOptions?: ErrorHandlerOptions;
}

/**
 * Wraps a function in try/catch and returns a result object with success status
 *
 * @param params - Parameters for handling a function
 * @param params.fn - The function to execute
 * @param params.context - Context information for error logging
 * @param params.options - Options to customize error handling
 * @returns Promise that resolves to TryFnResult
 */
const tryFn = async <T>(
  fn: TryFn<T>,
  { context, errorOptions = {} }: TryFnOptions
): Promise<TryFnResult<T>> => {
  try {
    const data = await fn();
    return {
      data,
      success: true,
    };
  } catch (error) {
    handleError({ error, context, options: errorOptions });

    return {
      error,
      success: false,
    };
  }
};

/**
 * Categorizes and logs errors based on their type and context
 */
const handleError = (params: HandleErrorParams) => {
  const { error, context = {}, options = {} } = params;
  const { customErrorMessage } = options;

  if (error instanceof ApolloError) {
    handleApolloError({ error, context, options });
  } else if (error instanceof Error) {
    // Standard JavaScript errors
    handleStandardError({ error, context, options });
  } else {
    // Unknown error type
    logger.error(
      customErrorMessage || 'unknown error type during apollo operation',
      {
        error,
        errorType: typeof error,
      }
    );
  }
};

const handleApolloError = (params: HandleErrorParams<ApolloError>) => {
  const { error, context, options = {} } = params;
  const { graphQLErrors, networkError } = error;
  const { customErrorMessage } = options;

  if (networkError) {
    // Network connectivity issues - log as warning, user should retry
    logger.warn(customErrorMessage || 'network error during apollo operation', {
      error: networkError,
      networkErrorName: networkError.name,
      networkErrorCode: (networkError as any).statusCode,
      errorType: 'network',
      context,
    });
  } else if (graphQLErrors.length > 0) {
    // GraphQL server errors - log as error, may need support intervention
    const graphQLError = graphQLErrors[0];
    logger.error(
      customErrorMessage || 'graphql error during apollo operation',
      {
        error: graphQLError,
        graphQLErrorMessage: graphQLError.message,
        graphQLErrorExtensions: graphQLError.extensions,
        graphQLErrorPath: graphQLError.path,
        errorType: 'graphql',
        context,
      }
    );
  } else {
    // Generic Apollo error
    logger.error(customErrorMessage || 'apollo error during operation', {
      error,
      errorType: 'apollo',
      context,
    });
  }
};

const handleStandardError = (params: HandleErrorParams<Error>) => {
  const { error, context, options = {} } = params;
  const { customErrorMessage, includeStackTrace = true } = options;

  logger.error(customErrorMessage || 'standard error during operation', {
    error: error.message,
    errorName: error.name,
    errorStack: includeStackTrace ? error.stack : undefined,
    errorType: 'standard',
    context,
  });
};

export { tryFn };
